/// <amd-module name="Artifacts/Root/redux/Actions" />
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */

import { ICurrentModal } from "Artifacts/Root/redux/Contracts";
import { IFeed } from "Artifacts/Root/types";
import { FeedMessage, ViewIdentifier } from "Artifacts/Root/types/AzureArtifacts";
import {
  ActionsUnion,
  ActionWithPayload,
  createAction,
  trackedAction,
  trackedErrorAction,
} from "redux/Utils/ActionHelper";

export const enum RootActionTypes {
  ResolveFeed = "Artifacts/Root/ResolveFeed",
  ResolveView = "Artifacts/Root/ResolveView",
  FakeDeleteView = "Artifacts/Root/FakeDeleteView",
  FakeAddOrEditView = "Artifacts/Root/FakeAddOrEditView",

  LoadFeedMessages = "Artifacts/Root/LoadFeedMessages",
  ShowFeedMessages = "Artifacts/Root/ShowFeedMessages",
  HideFeedMessage = "Artifacts/Root/HideFeedMessages",

  ShowModal = "Artifacts/Root/ShowModal",
  HideModal = "Artifacts/Root/HideModal",

  PromoteCommandStarted = "Artifacts/Root/PromoteCommandStarted",
  PromoteCommandCompleted = "Artifacts/Root/PromoteCommandCompleted",
  PromoteCommandFailed = "Artifacts/Root/PromoteCommandFailed",

  PermDeleteCommandStarted = "Artifacts/Root/PermDeleteCommandStarted",
  PermDeleteCommandCompleted = "Artifacts/Root/PermDeleteCommandCompleted",
  PermDeleteCommandFailed = "Artifacts/Root/PermDeleteCommandFailed",

  SoftDeleteCommandStarted = "Artifacts/Root/SoftDeleteCommandStarted",
  SoftDeleteCommandCompleted = "Artifacts/Root/SoftDeleteCommandCompleted",
  SoftDeleteCommandFailed = "Artifacts/Root/SoftDeleteCommandFailed",

  UnlistRelistCommandStarted = "Artifacts/Root/UnlistRelistCommandStarted",
  UnlistRelistCommandCompleted = "Artifacts/Root/UnlistRelistCommandCompleted",
  UnlistRelistCommandFailed = "Artifacts/Root/UnlistRelistCommandFailed",

  RestoreCommandStarted = "Artifacts/Root/RestoreCommandStarted",
  RestoreCommandCompleted = "Artifacts/Root/RestoreCommandCompleted",
  RestoreCommandFailed = "Artifacts/Root/RestoreCommandFailed",

  DeprecateCommandStarted = "Artifacts/Root/DeprecateCommandStarted",
  DeprecateCommandCompleted = "Artifacts/Root/DeprecateCommandCompleted",
  DeprecateCommandFailed = "Artifacts/Root/DeprecateCommandFailed",
}

const TelemetryModalIdKey = "ModalId";

export interface IResolveFeedPayload {
  feed: IFeed;
}

export interface IResolveViewPayload {
  view?: ViewIdentifier;
}

export interface IAddOrEditOrDeleteViewPayload {
  view: ViewIdentifier;
}

export interface ILoadFeedMessagesPayload {
  feed: IFeed;
}

export interface IShowFeedMessagesPayload {
  feedMessages: FeedMessage[];
}

export interface IHideFeedMessagePayload {
  messageIndex: number;
}

export interface IShowModalPayload {
  modal: ICurrentModal<any>;
}

export interface ICommandFailedPayload {
  error: string;
}

export const RootActions = {
  resolveFeed: (feed: IFeed) =>
    createAction<RootActionTypes, IResolveFeedPayload>(RootActionTypes.ResolveFeed, {
      feed,
    }),
  resolveView: (view?: ViewIdentifier) =>
    createAction<RootActionTypes, IResolveViewPayload>(RootActionTypes.ResolveView, {
      view,
    }),
  fakeDeleteView: (view: ViewIdentifier) =>
    createAction<RootActionTypes, IAddOrEditOrDeleteViewPayload>(RootActionTypes.FakeDeleteView, {
      view,
    }),
  fakeAddOrEditView: (view: ViewIdentifier) =>
    createAction<RootActionTypes, IAddOrEditOrDeleteViewPayload>(
      RootActionTypes.FakeAddOrEditView,
      {
        view,
      },
    ),
  loadFeedMessages: (feed: IFeed) =>
    createAction<RootActionTypes, ILoadFeedMessagesPayload>(RootActionTypes.LoadFeedMessages, {
      feed,
    }),
  showFeedMessages: (feedMessages: FeedMessage[]) =>
    createAction<RootActionTypes, IShowFeedMessagesPayload>(RootActionTypes.ShowFeedMessages, {
      feedMessages,
    }),
  hideFeedMessage: (messageIndex: number) =>
    createAction<RootActionTypes, IHideFeedMessagePayload>(RootActionTypes.HideFeedMessage, {
      messageIndex,
    }),
  showModal: (modal: ICurrentModal<any>, modalIdForTelemetry?: string) =>
    trackedAction(
      createAction<RootActionTypes, IShowModalPayload>(RootActionTypes.ShowModal, { modal }),
      { [TelemetryModalIdKey]: modalIdForTelemetry || modal.modalId },
    ),
  hideModal: () => trackedAction(createAction(RootActionTypes.HideModal)),
  promoteCommandStarted: () => createAction(RootActionTypes.PromoteCommandStarted),
  promoteCommandCompleted: () =>
    trackedAction(createAction(RootActionTypes.PromoteCommandCompleted)),
  promoteCommandFailed: (error: string) =>
    trackedErrorAction(
      createAction<RootActionTypes, ICommandFailedPayload>(RootActionTypes.PromoteCommandFailed, {
        error,
      }),
    ),
  permDeleteCommandStarted: () => createAction(RootActionTypes.PermDeleteCommandStarted),
  permDeleteCommandCompleted: () =>
    trackedAction(createAction(RootActionTypes.PermDeleteCommandCompleted)),
  permDeleteCommandFailed: (error: string) =>
    trackedErrorAction(
      createAction<RootActionTypes, ICommandFailedPayload>(
        RootActionTypes.PermDeleteCommandFailed,
        {
          error,
        },
      ),
    ),
  softDeleteCommandStarted: () => createAction(RootActionTypes.SoftDeleteCommandStarted),
  softDeleteCommandCompleted: () =>
    trackedAction(createAction(RootActionTypes.SoftDeleteCommandCompleted)),
  softDeleteCommandFailed: (error: string) =>
    trackedErrorAction(
      createAction<RootActionTypes, ICommandFailedPayload>(
        RootActionTypes.SoftDeleteCommandFailed,
        {
          error,
        },
      ),
    ),
  unlistRelistCommandStarted: () => createAction(RootActionTypes.UnlistRelistCommandStarted),
  unlistRelistCommandCompleted: () =>
    trackedAction(createAction(RootActionTypes.UnlistRelistCommandCompleted)),
  unlistRelistCommandFailed: (error: string) =>
    trackedAction(
      createAction<RootActionTypes, ICommandFailedPayload>(
        RootActionTypes.UnlistRelistCommandFailed,
        {
          error,
        },
      ),
    ),
  restoreCommandStarted: () => createAction(RootActionTypes.RestoreCommandStarted),
  restoreCommandCompleted: () =>
    trackedAction(createAction(RootActionTypes.RestoreCommandCompleted)),
  restoreCommandFailed: (error: string) =>
    trackedErrorAction(
      createAction<RootActionTypes, ICommandFailedPayload>(RootActionTypes.RestoreCommandFailed, {
        error,
      }),
    ),
  deprecateCommandStarted: () => createAction(RootActionTypes.DeprecateCommandStarted),
  deprecateCommandCompleted: () =>
    trackedAction(createAction(RootActionTypes.DeprecateCommandCompleted)),
  deprecateCommandFailed: (error: string) =>
    trackedErrorAction(
      createAction<RootActionTypes, ICommandFailedPayload>(RootActionTypes.DeprecateCommandFailed, {
        error,
      }),
    ),
};

export function trackActionWithCount<T extends string, P>(
  action: ActionWithPayload<T, P>,
  versionCount: number,
): ActionWithPayload<T, P> {
  return trackedAction(action, { ["Count"]: versionCount });
}

export type RootActions = ActionsUnion<typeof RootActions>;
